/**
 * Tour Calendar Filter - Modern JavaScript
 * Mit Metafeld-Unterstützung und korrekter mehrtägiger Events
 * Mehrtägig = Datumsbereich in Option1 (z.B. "31.01. - 01.02.2026")
 */

if (!customElements.get('tour-calendar-filter')) {
  class TourCalendarFilter extends HTMLElement {
    constructor() {
      super();
      this.products = [];
      this.filtered = [];
      this.view = this.dataset.view || 'grid';
      this.month = new Date().getMonth();
      this.year = new Date().getFullYear();
      this.months = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
    }

    connectedCallback() {
      this.loadData();
      this.bind();
      this.setView(this.view);
      if (this.view === 'calendar') this.renderCalendar();
    }

    // ==========================================
    // Data Loading
    // ==========================================

    loadData() {
      const id = this.closest('[data-section-id]')?.dataset.sectionId;
      const script = document.getElementById(`tcf-data-${id}`);
      if (!script) return;

      try {
        const data = JSON.parse(script.textContent);
        this.products = (data.products || []).map(p => this.enrichProduct(p));
        this.filtered = [...this.products];
      } catch (e) {
        console.error('TCF: JSON parse error', e);
      }
    }

    enrichProduct(p) {
      // Parse Daten aus Varianten-Option1
      let variantDates = [];
      const seenDates = new Set();

      if (p.variants) {
        p.variants.forEach(v => {
          if (v.option1) {
            const parsed = this.parseVariantDate(v.option1);
            if (parsed) {
              const key = `${parsed.start.getTime()}-${parsed.end.getTime()}`;
              if (!seenDates.has(key)) {
                seenDates.add(key);
                variantDates.push(parsed);
              }
            }
          }
        });
      }

      // Sortiere nach Startdatum
      variantDates.sort((a, b) => a.start - b.start);

      // Nächstes Datum (in der Zukunft)
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const nextDate = variantDates.find(vd => vd.start >= now)?.start || variantDates[0]?.start || null;

      return {
        ...p,
        variantDates,
        nextDate
      };
    }

    /**
     * Parse Datum aus Option1 Value
     * Unterstützt:
     * - "31.01. - 01.02.2026" (mehrtägig, unterschiedliche Monate)
     * - "15.-18.06.2026" (mehrtägig, gleicher Monat)
     * - "14.03.2026" (einzeln)
     * - "Samstag 16.05.2026" (mit Wochentag)
     */
    parseVariantDate(str) {
      if (!str || typeof str !== 'string') return null;

      // Entferne Wochentag am Anfang
      let s = str.replace(/^(Montag|Dienstag|Mittwoch|Donnerstag|Freitag|Samstag|Sonntag)\s+/i, '');

      // Format: DD.MM. - DD.MM.YYYY (mit Leerzeichen und Punkt nach erstem Monat)
      let m = s.match(/(\d{1,2})\.(\d{1,2})\.\s*-\s*(\d{1,2})\.(\d{1,2})\.(\d{4})/);
      if (m) {
        const start = new Date(+m[5], +m[2] - 1, +m[1]);
        const end = new Date(+m[5], +m[4] - 1, +m[3]);
        return {
          start,
          end,
          isMultiDay: start.getTime() !== end.getTime(),
          original: str
        };
      }

      // Format: DD.-DD.MM.YYYY (gleicher Monat)
      m = s.match(/(\d{1,2})\.\s*-\s*(\d{1,2})\.(\d{1,2})\.(\d{4})/);
      if (m) {
        const start = new Date(+m[4], +m[3] - 1, +m[1]);
        const end = new Date(+m[4], +m[3] - 1, +m[2]);
        return {
          start,
          end,
          isMultiDay: start.getTime() !== end.getTime(),
          original: str
        };
      }

      // Format: DD.MM.YYYY (einzelnes Datum)
      m = s.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/);
      if (m) {
        const d = new Date(+m[3], +m[2] - 1, +m[1]);
        return {
          start: d,
          end: d,
          isMultiDay: false,
          original: str
        };
      }

      return null;
    }

    // ==========================================
    // Event Binding
    // ==========================================

    bind() {
      // View Toggle
      this.querySelectorAll('[data-view]').forEach(btn => {
        btn.addEventListener('click', () => this.setView(btn.dataset.view));
      });

      // Filters
      this.querySelectorAll('[data-filter]').forEach(el => {
        el.addEventListener('change', () => this.filter());
      });

      // Sort
      this.querySelector('[data-sort]')?.addEventListener('change', () => this.filter());

      // Clear
      this.querySelectorAll('[data-clear-filters]').forEach(btn => {
        btn.addEventListener('click', () => this.clearFilters());
      });

      // Calendar Nav
      this.querySelector('[data-cal-prev]')?.addEventListener('click', () => this.navCalendar(-1));
      this.querySelector('[data-cal-next]')?.addEventListener('click', () => this.navCalendar(1));

      // Day Detail Close
      this.querySelector('[data-day-detail-close]')?.addEventListener('click', () => this.closeDayDetail());
    }

    // ==========================================
    // View Management
    // ==========================================

    setView(view) {
      this.view = view;

      this.querySelectorAll('[data-view]').forEach(btn => {
        btn.classList.toggle('is-active', btn.dataset.view === view);
      });

      this.querySelectorAll('[data-view-content]').forEach(el => {
        const isGrid = el.dataset.viewContent === 'grid';
        const isCalendar = el.dataset.viewContent === 'calendar';
        el.classList.toggle('is-active', (view === 'grid' || view === 'list') && isGrid || view === 'calendar' && isCalendar);
      });

      const grid = this.querySelector('[data-grid]');
      if (grid) {
        grid.classList.toggle('tcf__grid--list', view === 'list');
      }

      if (view === 'calendar') this.renderCalendar();
    }

    // ==========================================
    // Filtering & Sorting
    // ==========================================

    filter() {
      const collection = this.querySelector('[data-filter="collection"]')?.value || '';
      const region = this.querySelector('[data-filter="region"]')?.value || '';
      const type = this.querySelector('[data-filter="type"]')?.value || '';
      const level = this.querySelector('[data-filter="level"]')?.value || '';
      const date = this.querySelector('[data-filter="date"]')?.value || '';
      const price = this.querySelector('[data-filter="price"]')?.value || '';
      const sort = this.querySelector('[data-sort]')?.value || 'date_asc';

      const filterDate = date ? new Date(date) : null;
      if (filterDate) filterDate.setHours(0, 0, 0, 0);

      const maxPrice = price ? parseInt(price, 10) * 100 : null;

      this.filtered = this.products.filter(p => {
        // Collection
        if (collection && p.collectionHandle !== collection) return false;

        // Region
        if (region && p.meta?.region !== region) return false;

        // Type (Fortbewegungsart)
        if (type) {
          const types = p.meta?.fortbewegungsart || [];
          if (!types.includes(type)) return false;
        }

        // Level
        if (level) {
          const levels = p.meta?.level || [];
          if (!levels.includes(level)) return false;
        }

        // Date
        if (filterDate) {
          const hasValidDate = p.variantDates.some(vd => vd.end >= filterDate);
          if (!hasValidDate && p.variantDates.length > 0) return false;
        }

        // Price
        if (maxPrice && p.price > maxPrice) return false;

        return true;
      });

      this.sort(sort);
      this.updateGrid();
      this.renderCalendar();
      this.updateCount();
      this.toggleEmpty();
      this.toggleClearBtn();
    }

    sort(by) {
      const far = new Date(9999, 11, 31);

      this.filtered.sort((a, b) => {
        switch (by) {
          case 'date_asc':
            return (a.nextDate || far) - (b.nextDate || far);
          case 'date_desc':
            return (b.nextDate || new Date(0)) - (a.nextDate || new Date(0));
          case 'price_asc':
            return a.price - b.price;
          case 'price_desc':
            return b.price - a.price;
          case 'title_asc':
            return a.title.localeCompare(b.title, 'de');
          default:
            return 0;
        }
      });
    }

    clearFilters() {
      this.querySelectorAll('[data-filter]').forEach(el => {
        if (el.tagName === 'SELECT') el.value = '';
        else el.value = '';
      });
      const sort = this.querySelector('[data-sort]');
      if (sort) sort.value = 'date_asc';
      this.filter();
    }

    toggleClearBtn() {
      const hasFilters = Array.from(this.querySelectorAll('[data-filter]')).some(el => el.value);
      this.querySelectorAll('[data-clear-filters]').forEach(btn => {
        btn.classList.toggle('is-visible', hasFilters);
      });
    }

    // ==========================================
    // UI Updates
    // ==========================================

    updateGrid() {
      const cards = this.querySelectorAll('.tcf-card');
      cards.forEach(card => {
        const id = parseInt(card.dataset.productId);
        const visible = this.filtered.some(p => p.id === id);
        card.style.display = visible ? '' : 'none';
      });
    }

    updateCount() {
      const el = this.querySelector('[data-count]');
      if (el) el.textContent = this.filtered.length;
    }

    toggleEmpty() {
      const empty = this.querySelector('[data-empty]');
      const gridContent = this.querySelector('[data-view-content="grid"]');
      const hasResults = this.filtered.length > 0;

      if (empty) {
        empty.classList.toggle('is-visible', !hasResults);
        empty.hidden = hasResults;
      }

      if (gridContent && gridContent.classList.contains('is-active')) {
        gridContent.style.display = hasResults ? '' : 'none';
      }
    }

    // ==========================================
    // Calendar
    // ==========================================

    navCalendar(dir) {
      this.month += dir;
      if (this.month > 11) { this.month = 0; this.year++; }
      if (this.month < 0) { this.month = 11; this.year--; }
      this.renderCalendar();
    }

    renderCalendar() {
      const grid = this.querySelector('[data-cal-grid]');
      const title = this.querySelector('[data-cal-title]');
      if (!grid) return;

      if (title) title.textContent = `${this.months[this.month]} ${this.year}`;

      const firstDay = new Date(this.year, this.month, 1);
      const lastDay = new Date(this.year, this.month + 1, 0);
      const daysInMonth = lastDay.getDate();
      let startDay = firstDay.getDay() - 1;
      if (startDay < 0) startDay = 6;

      const tourMap = this.getToursForMonth();

      grid.innerHTML = '';
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      for (let i = 0; i < startDay; i++) {
        grid.appendChild(this.createDayCell(null, today, tourMap));
      }

      for (let day = 1; day <= daysInMonth; day++) {
        grid.appendChild(this.createDayCell(day, today, tourMap));
      }
    }

    createDayCell(day, today, tourMap) {
      const cell = document.createElement('div');
      cell.className = 'tcf__cal-day';

      if (!day) {
        cell.classList.add('tcf__cal-day--empty');
        return cell;
      }

      const date = new Date(this.year, this.month, day);
      const dateKey = this.dateKey(date);
      const tours = tourMap[dateKey] || [];

      const num = document.createElement('span');
      num.className = 'tcf__cal-day-num';
      num.textContent = day;
      cell.appendChild(num);

      if (date.getTime() === today.getTime()) {
        cell.classList.add('tcf__cal-day--today');
      }

      if (date < today) {
        cell.classList.add('tcf__cal-day--past');
      }

      if (tours.length > 0) {
        cell.classList.add('tcf__cal-day--has-tours');

        const hasMultiDay = tours.some(t => t.isMultiDay);
        if (hasMultiDay) {
          cell.classList.add('tcf__cal-day--multiday');
        }

        const events = document.createElement('div');
        events.className = 'tcf__cal-events';

        tours.slice(0, 2).forEach(tour => {
          const ev = document.createElement('a');
          ev.className = 'tcf__cal-event';
          ev.href = tour.product.url;
          ev.textContent = tour.product.title.length > 12
            ? tour.product.title.substring(0, 10) + '...'
            : tour.product.title;
          ev.title = tour.product.title;

          // Styling basierend auf Mehrtages-Status
          if (!tour.isMultiDay) {
            ev.classList.add('tcf__cal-event--single');
          } else if (tour.dayType === 'start') {
            ev.classList.add('tcf__cal-event--start');
          } else if (tour.dayType === 'middle') {
            ev.classList.add('tcf__cal-event--middle');
          } else if (tour.dayType === 'end') {
            ev.classList.add('tcf__cal-event--end');
          } else {
            ev.classList.add('tcf__cal-event--multi');
          }

          events.appendChild(ev);
        });

        if (tours.length > 2) {
          const more = document.createElement('span');
          more.className = 'tcf__cal-more';
          more.textContent = `+${tours.length - 2} mehr`;
          events.appendChild(more);
        }

        cell.appendChild(events);

        cell.addEventListener('click', (e) => {
          if (e.target.tagName !== 'A') {
            this.showDayDetail(date, tours);
          }
        });
      }

      return cell;
    }

    getToursForMonth() {
      const map = {};

      this.filtered.forEach(p => {
        p.variantDates.forEach(vd => {
          this.addTourToMap(map, p, vd.start, vd.end, vd.isMultiDay);
        });
      });

      return map;
    }

    addTourToMap(map, product, start, end, isMultiDay) {
      const current = new Date(start);
      const endDate = new Date(end);

      while (current <= endDate) {
        if (current.getMonth() === this.month && current.getFullYear() === this.year) {
          const key = this.dateKey(current);
          if (!map[key]) map[key] = [];

          let dayType = 'single';
          if (isMultiDay) {
            if (current.getTime() === start.getTime()) {
              dayType = 'start';
            } else if (current.getTime() === endDate.getTime()) {
              dayType = 'end';
            } else {
              dayType = 'middle';
            }
          }

          const exists = map[key].some(t => t.product.id === product.id);
          if (!exists) {
            map[key].push({
              product,
              dayType,
              isMultiDay,
              start,
              end: endDate
            });
          }
        }
        current.setDate(current.getDate() + 1);
      }
    }

    dateKey(d) {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }

    // ==========================================
    // Day Detail Popup
    // ==========================================

    showDayDetail(date, tours) {
      const detail = this.querySelector('[data-day-detail]');
      const title = this.querySelector('[data-day-detail-title]');
      const content = this.querySelector('[data-day-detail-content]');

      if (!detail || !content) return;

      const formatted = `${date.getDate()}. ${this.months[date.getMonth()]} ${date.getFullYear()}`;
      if (title) title.textContent = `Touren am ${formatted}`;

      content.innerHTML = '';

      const seen = new Set();
      tours.forEach(tour => {
        if (seen.has(tour.product.id)) return;
        seen.add(tour.product.id);

        const p = tour.product;
        const el = document.createElement('a');
        el.className = 'tcf__day-detail-tour';
        el.href = p.url;

        const badge = tour.isMultiDay ? '<span style="background:linear-gradient(135deg,#6366f1,#8b5cf6);color:#fff;padding:2px 6px;border-radius:4px;font-size:0.65rem;margin-left:6px;">Mehrtägig</span>' : '';

        el.innerHTML = `
          <img src="${p.image || ''}" alt="${p.title}" loading="lazy">
          <div class="tcf__day-detail-tour-info">
            <h5>${p.title}${badge}</h5>
            <p>${p.meta?.region || ''} ${p.meta?.tourDauer ? '• ' + p.meta.tourDauer : ''}</p>
            <span class="tcf__day-detail-tour-price">${p.priceFormatted}</span>
          </div>
        `;

        content.appendChild(el);
      });

      detail.hidden = false;
      requestAnimationFrame(() => {
        detail.classList.add('is-open');
      });
    }

    closeDayDetail() {
      const detail = this.querySelector('[data-day-detail]');
      if (!detail) return;

      detail.classList.remove('is-open');
      setTimeout(() => {
        detail.hidden = true;
      }, 300);
    }
  }

  customElements.define('tour-calendar-filter', TourCalendarFilter);
}
